import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FM220, FM221, m209Api } from '../../services/m209Api';
import { useM209Mode } from './hooks/useM209Mode';
import { useM209Record } from './hooks/useM209Record';
import { useM209Navigation } from './hooks/useM209Navigation';
import { SharedActionBar } from '../../components/SharedActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import './M209Form.css';

const M209Form: React.FC = () => {
  const navigate = useNavigate();

  // Hooks
  const { mode, setMode, isEditing } = useM209Mode();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useM209Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useM209Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load M209 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing && mode === 'add') {
      await handleSave(mode, async (result: FM220) => {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fm220_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(async () => {
    if (isEditing && mode === 'edit') {
      await handleSave(mode, async (result: FM220) => {
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('edit');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除修改單 ${record?.fm220_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  // Direct approve (FoxPro Command2 "核准" → u_vchk_fm030)
  const handleApprove = useCallback(() => {
    if (!record?.fm220_01) return;
    Modal.confirm({
      title: '核准確認',
      content: '請確認核准本資料!',
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await m209Api.approve(record.fm220_01);
          applyResult(result);
          message.success('核准成功');
        } catch {
          message.error('核准失敗');
        }
      },
    });
  }, [record, applyResult]);

  const handleExit = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      navigate('/erp');
    }
  }, [isEditing, revertToRecord, setMode, navigate]);

  // Save (for SharedActionBar onSave)
  const handleSaveAction = useCallback(async () => {
    if (mode === 'add') {
      await handleAddAction();
    } else if (mode === 'edit') {
      await handleModifyAction();
    }
  }, [mode, handleAddAction, handleModifyAction]);

  // Cancel (for SharedActionBar onCancel)
  const handleCancelAction = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  /** Handle detail field changes */
  const handleDetailChange = useCallback((idx: number, key: keyof FM221, value: FM221[keyof FM221]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  // Status code for approval display
  const statusCode = (record?.fm220_80 || '').trim();

  return (
    <div className="m209-form">
      <div className="page-wrapper">
        {/* Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a>
              <span>/</span>
              <span className="current">研發架號修改單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>研發架號修改單 (pwm209f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fm220_01 || '(新增)'}
            </span>
          </div>
        </div>

        {/* Single screen — no tabs (FoxPro pageCount=0) */}
        <div className="form-content">
          <Tab1BasicInfo
            formData={formData}
            isEditing={isEditing}
            getField={getField}
            setField={setField}
            isApproved={statusCode === 'P'}
          />
          <Tab2Details
            details={details}
            isEditing={isEditing}
            onAdd={addDetailRow}
            onDelete={deleteDetailRow}
            onDetailChange={handleDetailChange}
            totalCount={Number(getField('fm220_15')) || details.length}
          />
        </div>

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAddAction}
          onModify={() => { if (!record) return; setMode('edit'); }}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancelAction}
          approval={{
            statusCode,
            isApproved: statusCode === 'P',
            isPending: statusCode !== 'P' && !!record,
            isLocked: statusCode === 'P',
          }}
          onApprove={handleApprove}
          onExit={handleExit}
        />
      </div>
    </div>
  );
};

export default M209Form;
